import { useState, useEffect, useRef } from 'react';
import { KpiTile, AreaTrend, Badge, DeltaTag, Icon, cx, Donut } from '../lib/shared';
import type { KpiTileData } from '../lib/shared';
import apiClient from '../lib/api-client';

// KPI / KRI Dashboard
const PERIODS = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'ytd', label: 'Year to date' },
];

interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

interface FilterChipProps {
  icon?: string;
  label: string;
  value?: string;
  options: FilterOption[];
  selectedId: string;
  onSelect: (id: string) => void;
}

function FilterChip({ icon, label, value, options, selectedId, onSelect }: FilterChipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.id === selectedId);
  const displayValue = selectedOption?.label || value || 'All';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cx(
          'inline-flex items-center gap-2 h-9 px-3 rounded-[8px] border transition',
          'font-display text-[13px]',
          selectedId !== 'all'
            ? 'border-ink bg-white text-ink font-semibold'
            : 'border-[#F3E4C8] bg-white text-ink/70 hover:border-ink/40'
        )}
      >
        {icon && <Icon name={icon} size={14} stroke="currentColor" />}
        <span>{label}</span>
        <span className="text-ink/45 font-data">{displayValue}</span>
        <Icon 
          name="chevron-down" 
          size={12} 
          stroke="currentColor" 
          className={cx('transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-white border border-[#F3E4C8] rounded-[8px] shadow-lg z-50">
          <div className="p-1">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onSelect(option.id);
                  setIsOpen(false);
                }}
                className={cx(
                  'w-full text-left px-3 py-2 rounded-[6px] font-display text-[13px] transition flex items-center justify-between',
                  selectedId === option.id
                    ? 'bg-[#FCF8F1] text-ink font-semibold'
                    : 'hover:bg-[#FCF8F1] text-ink/70'
                )}
              >
                <span>{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-ink/45 font-data text-[11px]">{option.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface PeriodSwitcherProps {
  value: string;
  onChange: (period: string) => void;
}

function PeriodSwitcher({ value, onChange }: PeriodSwitcherProps) {
  return (
    <div className="inline-flex p-[3px] rounded-[8px] bg-[#F7EDDB] border border-[#F3E4C8]">
      {PERIODS.map((p) => (
        <button
          type="button"
          key={p.id}
          onClick={() => onChange(p.id)}
          className={cx(
            'px-3 h-7 rounded-[6px] font-display text-[12px] font-semibold transition',
            value === p.id
              ? 'bg-white text-ink shadow-[0_1px_2px_0_rgba(0,0,0,0.06)]'
              : 'text-ink/55 hover:text-ink'
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── KRI Strip ──────────────────────────────────────────────────────────────
interface KriData {
  id: string;
  label: string;
  value: number;
  of?: number;
  severity: 'critical' | 'high' | 'warning' | 'info';
  delta: number;
  deltaUnit: string;
  deltaDirection: 'improving' | 'worsening' | 'flat';
}

interface StatusMixItem {
  label: string;
  value: number;
  color: string;
}

interface TopFailingControl {
  id: string;
  name: string;
  code: string;
  failing: number;
  total: number;
}

interface DashboardOverviewData {
  tiles?: KpiTileData[];
  kri?: KriData[];
  coverageTrend?: { series: any[]; labels: string[] };
  statusMix?: StatusMixItem[];
  topFailing?: TopFailingControl[];
}

interface KriStripProps {
  kris: KriData[];
  onPick?: (kri: KriData) => void;
}

function KriStrip({ kris, onPick }: KriStripProps) {
  const SEV = {
    critical: { tone: 'crit' as const, label: 'Critical' },
    high: { tone: 'crit' as const, label: 'High' },
    warning: { tone: 'warn' as const, label: 'Warning' },
    info: { tone: 'info' as const, label: 'Info' },
  };
  return (
    <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-[#F7EDDB] flex items-center justify-between bg-[#FCF8F1]/60">
        <div className="flex items-center gap-2">
          <Icon name="alert-triangle" size={16} stroke="#F40B0B" />
          <span className="font-display font-bold text-[14px] uppercase tracking-[0.04em] text-ink">
            Key Risk Indicators
          </span>
          <Badge tone="muted">Live</Badge>
        </div>
        <button
          type="button"
          className="font-display text-[13px] font-semibold text-[#FA500E] hover:underline inline-flex items-center gap-1"
        >
          View risks <Icon name="arrow-up-right" size={14} stroke="currentColor" />
        </button>
      </div>
      <div className="grid grid-cols-4 divide-x divide-[#F7EDDB]">
        {kris.map((k) => {
          const sev = SEV[k.severity] || SEV.info;
          return (
            <button
              type="button"
              key={k.id}
              onClick={() => onPick?.(k)}
              className="text-left p-5 hover:bg-[#FCF8F1] transition flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <Badge tone={sev.tone} dot>
                  {sev.label}
                </Badge>
                <DeltaTag delta={k.delta} unit={k.deltaUnit} direction={k.deltaDirection} />
              </div>
              <div>
                <div className="font-display text-[12px] font-semibold text-ink/55 uppercase tracking-[0.04em]">
                  {k.label}
                </div>
                <div className="flex items-baseline gap-2 mt-1.5">
                  <span
                    className="font-display font-bold text-ink"
                    style={{ fontSize: 32, lineHeight: '1' }}
                  >
                    {k.value}
                  </span>
                  {k.of != null && (
                    <span className="font-data text-[13px] text-ink/55">of {k.of}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Dashboard Component ────────────────────────────────────────────────
interface DashboardProps {
  className?: string;
}

export function Dashboard({ className }: DashboardProps) {
  const [period, setPeriod] = useState('30d');
  const [filters, setFilters] = useState({
    project: 'all',
    framework: 'all',
    repo: 'all',
  });
  
  const [filterOptions, setFilterOptions] = useState({
    projects: [{ id: 'all', label: 'All Projects', count: undefined }] as FilterOption[],
    frameworks: [{ id: 'all', label: 'All Frameworks', count: undefined }] as FilterOption[],
    repos: [{ id: 'all', label: 'All Repositories', count: undefined }] as FilterOption[],
  });
  
  const [data, setData] = useState({
    tiles: [] as KpiTileData[],
    kri: [] as KriData[],
    coverageTrend: { series: [] as any[], labels: [] as string[] },
    statusMix: [] as StatusMixItem[],
    topFailing: [] as TopFailingControl[],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        // Load projects using compliance projects API
        const projectsRes = await apiClient.getComplianceProjects();
        if (projectsRes.success && projectsRes.data && typeof projectsRes.data === 'object' && 'projects' in projectsRes.data) {
          const projectsData = projectsRes.data as { projects: any[] };
          const projectOptions = [
            { id: 'all', label: 'All Projects', count: projectsData.projects.length },
            ...projectsData.projects.map((project: any) => ({
              id: project.id || project.name,
              label: project.name,
              count: project.repositoriesCount,
            }))
          ];
          
          setFilterOptions(prev => ({ ...prev, projects: projectOptions }));
          
          // Extract unique frameworks
          const allFrameworks = new Set<string>();
          projectsData.projects.forEach((project: any) => {
            project.metadata?.frameworks?.forEach((framework: string) => {
              allFrameworks.add(framework);
            });
          });
          
          if (allFrameworks.size > 0) {
            const frameworkOptions = [
              { id: 'all', label: 'All Frameworks', count: allFrameworks.size },
              ...Array.from(allFrameworks).map(framework => ({
                id: framework,
                label: framework.charAt(0).toUpperCase() + framework.slice(1),
                count: undefined,
              }))
            ];
            
            setFilterOptions(prev => ({ ...prev, frameworks: frameworkOptions }));
          }
        }

        // Load frameworks as well to populate framework filter
        const frameworksRes = await apiClient.getFrameworks();
        if (frameworksRes.success && frameworksRes.data && typeof frameworksRes.data === 'object' && 'frameworks' in frameworksRes.data) {
          const frameworksData = frameworksRes.data as { frameworks: any[] };
          const frameworkOptions = [
            { id: 'all', label: 'All Frameworks', count: frameworksData.frameworks.length },
            ...frameworksData.frameworks.map((framework: any) => ({
              id: framework.id || framework.name,
              label: framework.name || framework.title,
              count: framework.controlsCount,
            }))
          ];
          
          setFilterOptions(prev => ({ ...prev, frameworks: frameworkOptions }));
        }

        // For repositories, we'll create demo options since there's no direct endpoint
        // In a real implementation, this would come from a repositories service
        const demoRepoOptions = [
          { id: 'all', label: 'All Repositories', count: undefined },
          { id: 'backend-api', label: 'Backend API', count: 15 },
          { id: 'frontend-app', label: 'Frontend App', count: 8 },
          { id: 'data-pipeline', label: 'Data Pipeline', count: 12 },
          { id: 'auth-service', label: 'Auth Service', count: 6 },
          { id: 'payment-service', label: 'Payment Service', count: 10 },
        ];
        
        setFilterOptions(prev => ({ ...prev, repos: demoRepoOptions }));
      } catch (err) {
        console.warn('Could not load filter options:', err);
        // Keep default options if API fails
      }
    };

    loadFilterOptions();
  }, []);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try multiple approaches to get data
        let dashboardData: DashboardOverviewData = {};

        // First, try the dashboard overview endpoint with filters
        try {
          const overviewRes = await apiClient.getDashboardOverview({ 
            period,
            project: filters.project !== 'all' ? filters.project : undefined,
            framework: filters.framework !== 'all' ? filters.framework : undefined,
            repo: filters.repo !== 'all' ? filters.repo : undefined,
          });
          if (overviewRes.success && overviewRes.data) {
            dashboardData = overviewRes.data as DashboardOverviewData;
          }
        } catch (overviewErr) {
          console.warn('Dashboard overview endpoint failed:', overviewErr);
        }

        // If no data yet, try getting individual components
        if (!dashboardData.tiles || dashboardData.tiles.length === 0) {
          try {
            // Try compliance metrics as a fallback
            const complianceRes = await apiClient.getComplianceMetrics();
            if (complianceRes.success && complianceRes.data) {
              // Transform compliance data to dashboard format
              const complianceData = complianceRes.data as any;
              dashboardData.tiles = [
                {
                  id: 'coverage',
                  label: 'Repository Coverage',
                  sublabel: 'Repos with compliance controls',
                  value: complianceData.coverage_percentage || 0,
                  unit: '%',
                  spark: Array.from({ length: 14 }, () => Math.floor(Math.random() * 10) + 65)
                },
                {
                  id: 'compliance-score',
                  label: 'Compliance Score', 
                  sublabel: 'Overall compliance rating',
                  value: complianceData.compliance_score || 0,
                  unit: '%',
                  spark: Array.from({ length: 14 }, () => Math.floor(Math.random() * 10) + 75)
                }
              ];
            }
          } catch (complianceErr) {
            console.warn('Compliance metrics endpoint failed:', complianceErr);
          }
        }

        // If still no data, create demo data for development
        if (!dashboardData.tiles || dashboardData.tiles.length === 0) {
          console.info('Using demo data - API services may not be available');
          dashboardData = {
            tiles: [
              {
                id: 'coverage',
                label: 'Repository Coverage',
                sublabel: 'Repos with ≥1 control applied',
                value: 76,
                unit: '%',
                delta: 4.2,
                deltaLabel: 'vs. previous 30d',
                spark: [62, 64, 63, 66, 68, 70, 71, 72, 71, 73, 74, 74, 75, 76],
                denominator: '38 / 50 repos'
              },
              {
                id: 'control-health',
                label: 'Control Health',
                sublabel: 'Controls passing across all repos',
                value: 84,
                unit: '%',
                delta: 1.6,
                deltaLabel: 'vs. previous 30d',
                spark: [78, 79, 80, 80, 82, 81, 82, 82, 83, 82, 83, 83, 84, 84],
                denominator: '421 / 502 control runs'
              },
              {
                id: 'active-controls',
                label: 'Active Controls',
                sublabel: 'Published, applied to a repo',
                value: 47,
                delta: 6,
                deltaUnit: 'abs',
                deltaLabel: 'added this period',
                spark: [38, 39, 39, 40, 41, 41, 42, 42, 43, 44, 45, 46, 46, 47],
                denominator: '47 of 63 in library'
              },
              {
                id: 'mttr',
                label: 'Mean Time to Remediate',
                sublabel: 'Open → passing across controls',
                value: 4.3,
                unit: 'd',
                delta: -0.7,
                deltaUnit: 'abs',
                deltaLabel: 'shorter than previous',
                deltaDirection: 'improving',
                spark: [6.1, 5.8, 5.6, 5.4, 5.5, 5.2, 5.0, 5.1, 4.8, 4.7, 4.6, 4.5, 4.4, 4.3],
                denominator: 'median across 84 issues'
              }
            ],
            kri: [
              {
                id: 'repos-at-risk',
                label: 'Repos with unaddressed risk',
                value: 12,
                of: 50,
                severity: 'high',
                delta: 2,
                deltaUnit: 'abs',
                deltaDirection: 'worsening'
              },
              {
                id: 'critical-open',
                label: 'Critical findings open',
                value: 5,
                severity: 'critical',
                delta: 1,
                deltaUnit: 'abs',
                deltaDirection: 'worsening'
              },
              {
                id: 'expiring',
                label: 'Risk acceptances expiring <30d',
                value: 3,
                severity: 'warning',
                delta: 0,
                deltaUnit: 'abs',
                deltaDirection: 'flat'
              },
              {
                id: 'uncovered-repos',
                label: 'Repos with no controls',
                value: 12,
                of: 50,
                severity: 'warning',
                delta: -2,
                deltaUnit: 'abs',
                deltaDirection: 'improving'
              }
            ],
            statusMix: [
              { label: 'Passing', value: 421, color: '#159D4C' },
              { label: 'Failing', value: 38, color: '#F40B0B' },
              { label: 'Pending', value: 28, color: '#CA9401' },
              { label: 'Expired', value: 15, color: '#6B7280' }
            ],
            coverageTrend: {
              series: [
                { name: 'Coverage', data: [68, 69, 70, 71, 72, 73, 73, 74, 75, 76, 76] },
                { name: 'Health', data: [80, 81, 81, 82, 82, 83, 83, 83, 84, 84, 84] }
              ],
              labels: ['Apr 20', 'Apr 23', 'Apr 26', 'Apr 29', 'May 02', 'May 05', 'May 08', 'May 11', 'May 14', 'May 17', 'May 19']
            },
            topFailing: [
              { id: 'ENC-AT-REST', name: 'Encryption data at rest', code: '#EDR01', failing: 9, total: 22 },
              { id: 'AUTH-MFA', name: 'Auth — MFA enforced', code: '#AUTH01', failing: 7, total: 31 },
              { id: 'DAZ', name: 'Data anonymization', code: '#DAZ01', failing: 6, total: 18 },
              { id: 'PGE', name: 'Purge of stale data', code: '#PGE01', failing: 5, total: 14 },
              { id: 'CM', name: 'Consent management', code: '#CM01', failing: 4, total: 12 }
            ]
          };
        }

        setData({
          tiles: dashboardData.tiles || [],
          kri: dashboardData.kri || [],
          coverageTrend: dashboardData.coverageTrend || { series: [], labels: [] },
          statusMix: dashboardData.statusMix || [],
          topFailing: dashboardData.topFailing || [],
        });

      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(`Error loading dashboard data: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [period, filters]);

  const handleKriClick = (kri: KriData) => {
    console.log('KRI clicked:', kri);
    // Handle navigation or modal opening
  };

  const handleTileClick = (tile: any) => {
    console.log('KPI tile clicked:', tile);
    // Handle navigation or drilling down
  };

  if (loading) {
    return (
      <div className={cx('flex items-center justify-center h-96', className)}>
        <div className="flex items-center gap-3 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cx('flex flex-col items-center justify-center h-96 text-center', className)}>
        <div className="mb-4 text-red-500">
          <Icon name="alert-triangle" size={48} stroke="currentColor" />
        </div>
        <h2 className="font-display font-bold text-ink text-[24px] mb-2">Error Loading Dashboard</h2>
        <p className="font-display text-[16px] text-ink/60 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-500 text-white rounded-[8px] font-display font-semibold hover:bg-primary-600 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={cx('space-y-8', className)}>
      {/* Header with filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-ink text-[28px] mb-2">
            Compliance Dashboard
          </h1>
          <p className="font-display text-ink/60 text-[16px]">
            Monitor your security posture and compliance metrics across all projects and repositories.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSwitcher value={period} onChange={setPeriod} />
          <button
            type="button"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-[8px] border border-[#F3E4C8] bg-white text-ink/70 hover:border-ink/40 font-display text-[13px] transition"
          >
            <Icon name="filter" size={14} stroke="currentColor" />
            Filters
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          icon="package"
          label="Projects"
          options={filterOptions.projects}
          selectedId={filters.project}
          onSelect={(id) => setFilters(f => ({ ...f, project: id }))}
        />
        <FilterChip
          icon="shield"
          label="Frameworks"
          options={filterOptions.frameworks}
          selectedId={filters.framework}
          onSelect={(id) => setFilters(f => ({ ...f, framework: id }))}
        />
        <FilterChip
          icon="book"
          label="Repositories"
          options={filterOptions.repos}
          selectedId={filters.repo}
          onSelect={(id) => setFilters(f => ({ ...f, repo: id }))}
        />
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {data.tiles.map((tile) => (
          <KpiTile 
            key={tile.id} 
            tile={tile} 
            onClick={() => handleTileClick(tile)}
            accent="#FA500E"
          />
        ))}
      </div>

      {/* Key Risk Indicators */}
      <KriStrip kris={data.kri} onPick={handleKriClick} />

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Coverage Trend Chart */}
        <div className="xl:col-span-2 rounded-[10px] border border-[#F3E4C8] bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display font-bold text-ink text-[18px]">Coverage Trend</h3>
              <p className="font-display text-ink/60 text-[14px] mt-1">
                Repository coverage and control health over time
              </p>
            </div>
            <button
              type="button"
              className="font-display text-[13px] text-ink/60 hover:text-ink inline-flex items-center gap-1.5"
            >
              <Icon name="external" size={13} stroke="currentColor" />
              Full view
            </button>
          </div>
          <div className="h-64">
            <AreaTrend data={data.coverageTrend} height={256} />
          </div>
        </div>

        {/* Status Mix Donut */}
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display font-bold text-ink text-[18px]">Control Status</h3>
              <p className="font-display text-ink/60 text-[14px] mt-1">
                Current status across all control runs
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <Donut data={data.statusMix} size={160} label="CONTROLS" />
            <div className="mt-6 w-full space-y-3">
              {data.statusMix.map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-display text-[13px] text-ink/70">{item.label}</span>
                  </div>
                  <span className="font-data text-[13px] font-semibold text-ink">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top Failing Controls */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white">
        <div className="px-6 py-4 border-b border-[#F7EDDB] flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-ink text-[18px]">Top Failing Controls</h3>
            <p className="font-display text-ink/60 text-[14px] mt-1">
              Controls with the highest failure rates across repositories
            </p>
          </div>
          <button
            type="button"
            className="font-display text-[13px] text-ink/60 hover:text-ink inline-flex items-center gap-1.5 border border-[#F3E4C8] rounded-[8px] px-2.5 h-8"
          >
            <Icon name="download" size={13} stroke="currentColor" />
            Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55 border-b border-[#F7EDDB]">
                <th className="text-left py-3 px-6">Control</th>
                <th className="text-left py-3 px-4">Code</th>
                <th className="text-right py-3 px-4">Failing</th>
                <th className="text-right py-3 px-4">Total</th>
                <th className="text-right py-3 px-6">Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.topFailing.map((control, i) => {
                const rate = Math.round((control.failing / control.total) * 100);
                const rateTone = rate >= 50 ? 'crit' : rate >= 25 ? 'warn' : 'ok';
                return (
                  <tr 
                    key={control.id} 
                    className={cx(
                      'border-b border-[#F7EDDB] hover:bg-[#FCF8F1] transition cursor-pointer',
                      i === data.topFailing.length - 1 && 'border-b-0'
                    )}
                  >
                    <td className="py-4 px-6">
                      <div className="font-display font-semibold text-ink text-[14px]">
                        {control.name}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <code className="font-mono text-[12px] text-ink/60 bg-[#F7EDDB] px-1.5 py-0.5 rounded">
                        {control.code}
                      </code>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="font-data font-semibold text-ink text-[14px]">
                        {control.failing}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="font-data text-ink/60 text-[14px]">
                        {control.total}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <Badge tone={rateTone}>
                        {rate}%
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;